import axios from 'axios'

/**
 * Merkezi hata yardımcıları (019). Backend hata gövdesi sözleşmesi `{ code }` + HTTP durum kodu
 * üzerinden çalışır. Ekranlardaki kopya `apiErrorCode` fonksiyonlarının yerine tek kaynak.
 */

/** Backend gövdesindeki iş/hata kodu (ör. OPTIMISTIC_LOCK_FAILURE, DUPLICATE_CODE). */
export function getApiErrorCode(err: unknown): string | undefined {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { code?: string } | undefined
    return data?.code
  }
  return undefined
}

/**
 * Backend gövdesindeki ihlal edilen **alan adı** (ör. `subActionsNotComplete`, `deadlineRequired`).
 * VALIDATION_ERROR gövdesi tek bir `code` taşır; hangi kuralın ihlal edildiğini `field` ayırt eder. Ekran,
 * alana göre açıklayıcı bir mesaj göstermek istediğinde kullanır.
 */
export function getApiErrorField(err: unknown): string | undefined {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { field?: string } | undefined
    return data?.field
  }
  return undefined
}

/**
 * Backend'in **lokalize** hata mesajı (Accept-Language'e göre TR/EN). Ekranlar, genel hata metni yerine
 * sunucunun açıklamasını göstermek istediğinde kullanır; yoksa undefined döner (çağıran genele düşer).
 */
export function getApiErrorMessage(err: unknown): string | undefined {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined
    const message = data?.message
    return typeof message === 'string' && message.trim() !== '' ? message : undefined
  }
  return undefined
}

/** HTTP durum kodu (ör. 400/401/403/404/409/500); yanıt yoksa undefined. */
export function getApiErrorStatus(err: unknown): number | undefined {
  if (axios.isAxiosError(err)) return err.response?.status
  return undefined
}

/** İstek iptal edildi mi (sayfa değişimi / abort). İptalde hiçbir bildirim gösterilmez. */
export function isCanceled(err: unknown): boolean {
  return axios.isCancel(err) || (axios.isAxiosError(err) && err.code === 'ERR_CANCELED')
}

/** Yanıt gövdesi yok mu (ağ hatası / zaman aşımı). */
export function isNetworkError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false
  if (isCanceled(err)) return false
  return !err.response
}

/** Eşzamanlı güncelleme çakışması (optimistic lock): 409 + OPTIMISTIC_LOCK_FAILURE. */
export function isOptimisticLock(err: unknown): boolean {
  return getApiErrorStatus(err) === 409 && getApiErrorCode(err) === 'OPTIMISTIC_LOCK_FAILURE'
}

/**
 * "Kayıt kullanımda" çakışması: 409 + INVALID_STATE.
 *
 * Sunucu, bir kaydın başka kayıtlar ona bağlı olduğu için silinemediğini bu kodla bildirir
 * (ör. 059: bağlı parametresi olan şablon parametre grubu). Merkezî hata yönetimi genel bir toast
 * basar; ekran bu yardımcıyla durumu ayırt edip **nedeni açıklayan** özel bir mesaj gösterebilir.
 */
export function isInUseConflict(err: unknown): boolean {
  return getApiErrorStatus(err) === 409 && getApiErrorCode(err) === 'INVALID_STATE'
}
